import {
  CalendarCheckLight,
  ConversationChat1Light,
  HourglassAlternateBold,
  RealEstateHouseLight,
  SingleNeutralCheckLight,
} from '@ha/icons';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { useIntl } from 'ha/i18n';
import { Typography, Grid, units } from '@hbf/dsl';
import { LongParagraph } from 'ha/ui.legacy/LongParagraph';
import { getResponseTimeString } from 'ha/utils/getResponseTimeString';
import { getLastActivityHumanized } from 'ha/utils/getLastActivityHumanized';
import { getAdvertiser, getAdvertiserInformation } from '../selectors';

const useStyles = makeStyles()(theme => ({
  icon: {
    fontSize: units.rem(0.75),
    marginRight: theme.spacing(1),
  },
  container: {
    fontSize: units.rem(0.85),
  },
  outlinedText: {
    color: theme.palette.secondary.main,
  },
  marginBottom: {
    marginBottom: theme.spacing(2),
  },
  iconsContainer: {
    marginTop: theme.spacing(6),
    textAlign: 'center',
  },
  iconCaption: {
    marginTop: theme.spacing(1),
  },
}));

const ListingAdvertiserDescription: React.FC = () => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();
  const advertiser = useSelector(getAdvertiser);
  const advertiserInformation = useSelector(getAdvertiserInformation);

  return (
    <div className={classes.container}>
      <Typography.H3 component="p" data-test-locator="Advertiser Details Name">
        {advertiser.firstName}
      </Typography.H3>

      {advertiser.location && (
        <Typography.Caption data-test-locator="Advertiser Details From">
          {T('from %s', advertiser.location)}
        </Typography.Caption>
      )}

      <Typography.Caption
        className={cx(classes.outlinedText, classes.marginBottom)}
        data-test-locator="Advertiser Details Lastseen"
      >
        {getLastActivityHumanized(T, advertiser.lastActivityAt)}
      </Typography.Caption>

      {advertiser.description && (
        <LongParagraph className={classes.marginBottom}>
          {advertiser.description}
        </LongParagraph>
      )}

      <div>
        {advertiser.responseRate > 0 && (
          <Typography.Caption data-test-locator="Advertiser Details Response Rate">
            <ConversationChat1Light className={classes.icon} />
            {`${T('Response rate')}: `}
            <Typography.Caption fontWeight="bold" display="inline">
              {Math.round(advertiser.responseRate * 100)}%
            </Typography.Caption>
          </Typography.Caption>
        )}

        {advertiser.responseTime > 0 && (
          <Typography.Caption data-test-locator="Advertiser Details Response Time">
            <HourglassAlternateBold className={classes.icon} />
            {`${T('Response time')}: `}
            <Typography.Caption fontWeight="bold" display="inline">
              {T(getResponseTimeString(advertiser.responseTime))}
            </Typography.Caption>
          </Typography.Caption>
        )}
      </div>

      <Grid container className={classes.iconsContainer} spacing={1}>
        {advertiserInformation.advertiserBookingsCount > 0 && (
          <Grid item data-test-locator="Advertiser Details Bookings">
            <CalendarCheckLight
              fontSize="large"
              className={classes.outlinedText}
            />
            <Typography.Paragraph
              fontWeight="bold"
              className={cx(classes.iconCaption, classes.outlinedText)}
            >
              {T(
                '%d successful',
                advertiserInformation.advertiserBookingsCount,
              )}
              <br />
              {advertiserInformation.advertiserBookingsCount === 1
                ? T('booking')
                : T('bookings')}
            </Typography.Paragraph>
          </Grid>
        )}

        {advertiser.createdAt && (
          <Grid item data-test-locator="Advertiser Details Member Since">
            <SingleNeutralCheckLight
              fontSize="large"
              className={classes.outlinedText}
            />
            <Typography.Paragraph
              fontWeight="bold"
              className={cx(classes.iconCaption, classes.outlinedText)}
            >
              {T('Member since')}
              <br />
              {dayjs(advertiser.createdAt).format('MMMM YYYY')}
            </Typography.Paragraph>
          </Grid>
        )}

        {advertiserInformation.listingsCount > 0 && (
          <Grid item data-test-locator="Advertiser Details Listings">
            <RealEstateHouseLight
              fontSize="large"
              className={classes.outlinedText}
            />
            <Typography.Paragraph
              fontWeight="bold"
              className={cx(classes.iconCaption, classes.outlinedText)}
            >
              {advertiserInformation.listingsCount > 1
                ? T('%d listings', advertiserInformation.listingsCount)
                : T('%d listing', advertiserInformation.listingsCount)}
            </Typography.Paragraph>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export { ListingAdvertiserDescription };
